import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import CoreMonetizationAd from '@wix/da-shared-react/pkg/CoreMonetizationAd';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import {
  getIsLoggedIn,
  getIsVerified,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import Zone, { ZoneGroup } from '../../Layout/Zone';
import { ZoneType } from '../../../../types/layout';
import { ModuleType } from '../../../../types/modules';
import TiersBox from '../../UserWidgets/Tiers/ProductBox';
import SubscriptionCenterBox from '../../UserWidgets/SubscriptionCenter';
import SubscriptionTipsBox from '../../UserWidgets/SubscriptionTips';
import TierContent from '../../UserWidgets/TierContent';
import SubscriptionsCoreOnlyBlock from './SubscriptionsCoreOnlyBlock';
import SubscriptionsVisitorEmptyState from './SubscriptionsVisitorEmptyState/SubscriptionsVisitorEmptyState';
import MoneyDeviationPublishedModal from '../_partials/MoneyDeviationPublishedModal';
import s from './SubscriptionsSection.scss';

export interface Props {
  isSectionLoading: boolean;
  isOwner: boolean;
  hasTiers: boolean;
  hasPublishedTiers: boolean;
  hasCore: boolean;
  profileOwnerUser: PapiUser;
  createTipJarTier: () => void;
  openTierModal: () => void;
}

const SubscriptionsSection: React.FC<Props> = ({
  isOwner,
  hasTiers,
  hasPublishedTiers,
  hasCore,
  profileOwnerUser,
  isSectionLoading,
  createTipJarTier,
  openTierModal,
}) => {
  const { t } = useTranslation();
  const { sendUserToJoinPage } = useRestrictToLoggedIn();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isVerified = useSelector(getIsVerified);
  const isMobile = useContext(MobileContext);
  const showExpiredCoreBanner = isOwner && hasTiers && !hasCore;

  const [showSuccessModal, setShowSuccessModal] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return !!new URLSearchParams(window.location.search).get(
      'tier-publish-success'
    );
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (window.location.search.includes('new_tier')) {
      if (!isLoggedIn) {
        sendUserToJoinPage(ReferralType.SUBSCRIPTIONS_TIER_CREATION);
      }
      if (isVerified && isOwner) {
        openTierModal();
      }
    }
    // run only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissSuccessModal = useCallback(() => setShowSuccessModal(false), []);

  if (isSectionLoading) {
    return <LoadingIndicator />;
  }

  if (isOwner) {
    if (!hasTiers) {
      return <SubscriptionsCoreOnlyBlock className={s['core-only-block']} />;
    }
  } else {
    if (!hasPublishedTiers) {
      return (
        <SubscriptionsVisitorEmptyState profileOwnerUser={profileOwnerUser} />
      );
    }
  }

  return (
    <div className={cn(s['root'], showExpiredCoreBanner && s['with-banner'])}>
      {showSuccessModal && (
        <MoneyDeviationPublishedModal
          type="tier"
          onClose={dismissSuccessModal}
        />
      )}
      {showExpiredCoreBanner && (
        <CoreMonetizationAd
          text={t('page.subscriptions.coreMonetizationAd.text')}
          withCTA
        />
      )}
      <ZoneGroup className={s['zone-group']}>
        {isOwner && (
          <Zone
            className={cn(s['zone'], s['top-zone'])}
            locked={true}
            type={ZoneType.MID_RIGHT}
            mapModuleTypeToWidgetComponent={
              isMobile
                ? {
                    [ModuleType.SUBSCRIPTION_CENTER]: SubscriptionCenterBox,
                  }
                : {
                    [ModuleType.SUBSCRIPTION_CENTER]: SubscriptionCenterBox,
                    [ModuleType.SUBSCRIPTION_TIPS]: SubscriptionTipsBox,
                  }
            }
          />
        )}
        <Zone
          className={s['zone']}
          locked={true}
          type={ZoneType.MID_RIGHT}
          mapModuleTypeToWidgetComponent={{
            [ModuleType.TIERS]: TiersBox,
            [ModuleType.TIER_CONTENT]: TierContent,
          }}
        />
      </ZoneGroup>
    </div>
  );
};
SubscriptionsSection.displayName = 'SubscriptionsSection';

export default SubscriptionsSection;
